import { collection, addDoc, getFirestore, Timestamp } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

const LOG_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week — matches the Firestore TTL policy on `expiresAt`

/**
 * Writes a debug log entry to Firestore so issues on devices without an attached
 * debugger (e.g. TestFlight builds inside the native WebView) can be diagnosed
 * remotely. Never throws — a logging failure must not affect the calling flow.
 */
export async function logClientEvent(
  source: string,
  event: string,
  data?: Record<string, unknown>,
): Promise<void> {
  try {
    const ref = collection(db, 'logs');
    await addDoc(ref, {
      source,
      event,
      ...data,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromMillis(Date.now() + LOG_TTL_MS),
    });
  } catch (error) {
    console.error('Error logging client event:', error);
  }
}

/**
 * Serializes an Error (or unknown thrown value) into a plain object safe to
 * store in Firestore — Error instances don't serialize via spread/JSON.
 */
export function serializeError(e: unknown): Record<string, unknown> {
  if (e instanceof Error) {
    return { errorName: e.name, errorMessage: e.message, errorStack: e.stack ?? null };
  }
  return { errorValue: String(e) };
}
